import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { hasDoctorTitle, providerDisplayName } from "@pontimed/types";
import { cn } from "@pontimed/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Clock,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  RotateCw,
  User as UserIcon,
  Video as VideoIcon,
  VideoOff,
} from "lucide-react";
import { useEffect, useState } from "react";

import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useWebRTC } from "@/lib/video/use-webrtc";

interface RoomInfo {
  role: "provider" | "patient";
  appointmentId: string;
  startsAt: string;
  endsAt: string;
  expiresAt: string;
  provider: {
    name: string;
    photoUrl: string | null;
    specialty: string | null;
    profession?: string | null;
    /** IANA zone of the provider's country; null when unknown. */
    timeZone?: string | null;
  } | null;
  patient: { name: string; photoUrl: string | null } | null;
}

export const Route = createFileRoute("/_authenticated/video/$token")({
  component: VideoRoomPage,
});

function VideoRoomPage() {
  const { token } = Route.useParams();
  const { t, i18n } = useLingui();
  const navigate = useNavigate();

  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [roomLoading, setRoomLoading] = useState(true);
  const [roomError, setRoomError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setRoomLoading(true);
    setRoomError(null);
    apiFetch
      .get<RoomInfo>(`/api/video/rooms/${token}`, { signal: controller.signal })
      .then((data) => setRoomInfo(data))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof ApiError) {
          if (err.status === 404)
            setRoomError(t`Video service is launching soon. Please call your provider directly.`);
          else if (err.status === 403) setRoomError(t`This video link has expired.`);
          // P16: never a raw "HTTP 500" — a sentence and a Retry.
          else setRoomError(t`We couldn't open the room. Try again in a moment.`);
        } else {
          setRoomError(t`We couldn't open the room. Try again in a moment.`);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRoomLoading(false);
      });
    return () => controller.abort();
  }, [token, t, reloadKey]);

  if (roomLoading) return <RoomSkeleton />;

  if (roomError || !roomInfo) {
    return (
      <RoomError
        message={roomError ?? t`Video room unavailable`}
        onRetry={() => setReloadKey((k) => k + 1)}
        onBack={() => navigate({ to: "/account/user/dashboard" })}
      />
    );
  }

  // D6: the API titles the name without a UI locale ("Dr."); re-title it in
  // the UI language (bg "Д-р", it "Dott.") — never for other professions.
  const room: RoomInfo = roomInfo.provider
    ? {
        ...roomInfo,
        provider: {
          ...roomInfo.provider,
          name: providerDisplayName(roomInfo.provider.name, {
            profession: roomInfo.provider.profession,
            locale: i18n.locale,
          }),
        },
      }
    : roomInfo;

  if (!started) {
    return (
      <PreJoinScreen
        roomInfo={room}
        onJoin={() => setStarted(true)}
        onBack={() => navigate({ to: "/account/user/dashboard" })}
      />
    );
  }

  return <ActiveCall token={token} roomInfo={room} />;
}

/* ────────── Pre-join screen ────────── */

function PreJoinScreen({
  roomInfo,
  onJoin,
  onBack,
}: {
  roomInfo: RoomInfo;
  onJoin: () => void;
  onBack: () => void;
}) {
  const otherParty = roomInfo.role === "provider" ? roomInfo.patient : roomInfo.provider;
  const otherPartyLabel = roomInfo.role === "provider" ? "patient" : "provider";
  // D6: "Doctor" only for doctors and dentists, "Specialist" otherwise.
  const providerIsDoctor = hasDoctorTitle(roomInfo.provider?.profession);

  return (
    <div className="bg-background min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-md">
        <div className="border-border bg-card rounded-2xl border p-6 shadow-sm">
          <PageHeader
            className="mb-5"
            title={<Trans>Ready to start?</Trans>}
            description={<Trans>You're about to join a private video consultation.</Trans>}
          />

          {otherParty && (
            <div className="bg-muted/50 mb-5 flex items-center gap-3 rounded-xl p-3">
              {otherParty.photoUrl ? (
                <img
                  src={otherParty.photoUrl}
                  alt=""
                  className="size-12 rounded-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="bg-muted flex size-12 items-center justify-center rounded-full">
                  <UserIcon className="text-muted-foreground size-6" aria-hidden />
                </div>
              )}
              <div>
                <p className="text-muted-foreground text-xs tracking-wider uppercase">
                  {otherPartyLabel === "patient" ? (
                    <Trans>Patient</Trans>
                  ) : providerIsDoctor ? (
                    <Trans>Provider</Trans>
                  ) : (
                    <Trans>Specialist</Trans>
                  )}
                </p>
                <p className="text-foreground font-semibold">{otherParty.name}</p>
              </div>
            </div>
          )}

          <DualTimezoneBlock
            startsAt={roomInfo.startsAt}
            endsAt={roomInfo.endsAt}
            providerTimeZone={roomInfo.provider?.timeZone ?? null}
          />

          <div className="border-border my-5 rounded-xl border p-4 text-xs">
            <div className="flex items-start gap-2">
              <VideoIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
              <div className="space-y-2">
                <p className="text-foreground font-medium">
                  <Trans>Camera & microphone access</Trans>
                </p>
                <p className="text-muted-foreground">
                  <Trans>
                    Your browser will ask for permission to use your camera and microphone.
                  </Trans>
                </p>
              </div>
            </div>
          </div>

          <Button className="h-12 w-full text-base font-semibold" size="lg" onClick={onJoin}>
            <VideoIcon className="mr-2 size-4" aria-hidden />
            <Trans>Join call</Trans>
          </Button>
          <Button variant="outline" size="touch" className="mt-2 w-full" onClick={onBack}>
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Dual-timezone display per memory project_dual_timezone_display.md.
 * Renders the appointment time in the visitor's local tz; when the
 * provider's own zone (their country's, from the API) has a different UTC
 * offset at the appointment time, adds the provider's local time too. Zones
 * are compared by offset, not by name: Europe/Sofia and Europe/Bucharest
 * read the same and need no second line (P17).
 */
function DualTimezoneBlock({
  startsAt,
  endsAt,
  providerTimeZone,
}: {
  startsAt: string;
  endsAt: string;
  providerTimeZone: string | null;
}) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const start = new Date(startsAt);
  const end = new Date(endsAt);

  const userTz =
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
  // An unknown zone gives NaN: no second line (formatting it would throw).
  const providerOffset = providerTimeZone ? utcOffsetMinutes(start, providerTimeZone) : Number.NaN;
  const showDual =
    Number.isFinite(providerOffset) && providerOffset !== utcOffsetMinutes(start, userTz);

  const fmt = (date: Date, tz: string) =>
    new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz,
      timeZoneName: "short",
    }).format(date);
  // The end time gets its own formatter — slicing it out of the weekday+date
  // string broke in locales that put the weekday elsewhere (hu "H 14:30").
  const endTime = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: userTz,
  }).format(end);

  return (
    <div className="bg-primary/5 border-primary/20 rounded-xl border p-3">
      <p className="text-muted-foreground text-xs tracking-wider uppercase">
        <Trans>Scheduled</Trans>
      </p>
      <p className="text-foreground mt-1 text-sm font-medium">{fmt(start, userTz)}</p>
      <p className="text-muted-foreground text-xs">— {endTime}</p>
      {showDual && providerTimeZone && (
        <p className="text-muted-foreground mt-2 text-xs">
          <Trans>Provider local time</Trans>: {fmt(start, providerTimeZone)}
        </p>
      )}
    </div>
  );
}

/** UTC offset of `tz` at `date`, in minutes (NaN for an unknown zone). */
function utcOffsetMinutes(date: Date, tz: string): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(date);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
    return Math.round((asUtc - date.getTime()) / 60_000);
  } catch {
    return Number.NaN;
  }
}

/* ────────── Active call ────────── */

function ActiveCall({ token, roomInfo }: { token: string; roomInfo: RoomInfo }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const {
    state,
    error,
    role,
    micOn,
    camOn,
    toggleMic,
    toggleCam,
    hangup,
    localVideoRef,
    remoteVideoRef,
  } = useWebRTC({ token, enabled: true });

  const onHangup = async () => {
    await hangup();
    void navigate({ to: "/account/user/dashboard" });
  };

  const otherParty = role === "provider" ? roomInfo.patient : roomInfo.provider;
  const otherPartyLabel = role === "provider" ? "patient" : "provider";

  return (
    // z-[60]: above the sticky AppHeader (z-30), the tab bar (z-40) and the
    // z-50 update/cookie banners; below NavigationProgress (z-[100]) and
    // toasts (z-[9999]).
    <div className="bg-background fixed inset-0 z-[60] flex flex-col">
      {/* Main video area */}
      <div className="relative flex-1 overflow-hidden bg-black">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* Local video PiP */}
        <div className="absolute right-4 bottom-4 z-10 aspect-video w-32 overflow-hidden rounded-xl border-2 border-white/20 bg-black shadow-lg sm:w-48">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
          />
          {!camOn && (
            <div className="bg-muted absolute inset-0 flex items-center justify-center">
              <VideoOff className="text-muted-foreground size-6" aria-hidden />
            </div>
          )}
        </div>

        {/* State overlay */}
        {state !== "connected" && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-4">
            <div className="max-w-sm text-center text-white">
              {state === "requesting_media" && (
                <>
                  <Loader2 className="mx-auto mb-3 size-8 animate-spin" aria-hidden />
                  <p className="font-medium">
                    <Trans>Requesting camera & mic…</Trans>
                  </p>
                </>
              )}
              {state === "connecting" && (
                <>
                  <Loader2 className="mx-auto mb-3 size-8 animate-spin" aria-hidden />
                  <p className="font-medium">
                    <Trans>Connecting…</Trans>
                  </p>
                </>
              )}
              {state === "waiting_peer" && (
                <>
                  <Clock className="mx-auto mb-3 size-8" aria-hidden />
                  <p className="font-medium">
                    <Trans>Waiting for the other party…</Trans>
                  </p>
                  {otherParty && (
                    <p className="mt-1 text-sm text-white/70">
                      <Trans>Waiting for {otherParty.name}</Trans>
                    </p>
                  )}
                </>
              )}
              {state === "reconnecting" && (
                <>
                  <Loader2 className="mx-auto mb-3 size-8 animate-spin" aria-hidden />
                  <p className="font-medium">
                    <Trans>Reconnecting…</Trans>
                  </p>
                </>
              )}
              {state === "failed" && (
                <>
                  <AlertTriangle className="mx-auto mb-3 size-8 text-red-400" aria-hidden />
                  <p className="font-medium">
                    <Trans>Connection failed</Trans>
                  </p>
                  {error && <p className="mt-1 text-sm text-white/70">{error}</p>}
                </>
              )}
              {state === "ended" && (
                <>
                  <PhoneOff className="mx-auto mb-3 size-8" aria-hidden />
                  <p className="font-medium">
                    <Trans>Call ended</Trans>
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Header overlay (connected) */}
        {otherParty && state === "connected" && (
          <div className="absolute top-4 left-4 z-10 rounded-xl bg-black/40 px-3 py-2 text-white backdrop-blur">
            <p className="text-xs tracking-wider text-white/70 uppercase">
              {otherPartyLabel === "patient" ? <Trans>Patient</Trans> : <Trans>Provider</Trans>}
            </p>
            <p className="font-semibold">{otherParty.name}</p>
          </div>
        )}
      </div>

      {/* Control bar */}
      <div className="bg-card border-border flex items-center justify-center gap-3 border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={toggleMic}
          className={cn(
            "flex size-12 items-center justify-center rounded-full transition-colors",
            micOn
              ? "bg-muted hover:bg-muted/80 text-foreground"
              : "bg-red-500 text-white hover:bg-red-600",
          )}
          aria-label={micOn ? t`Mute mic` : t`Unmute mic`}
        >
          {micOn ? (
            <Mic className="size-5" aria-hidden />
          ) : (
            <MicOff className="size-5" aria-hidden />
          )}
        </button>

        <button
          type="button"
          onClick={toggleCam}
          className={cn(
            "flex size-12 items-center justify-center rounded-full transition-colors",
            camOn
              ? "bg-muted hover:bg-muted/80 text-foreground"
              : "bg-red-500 text-white hover:bg-red-600",
          )}
          aria-label={camOn ? t`Turn off camera` : t`Turn on camera`}
        >
          {camOn ? (
            <VideoIcon className="size-5" aria-hidden />
          ) : (
            <VideoOff className="size-5" aria-hidden />
          )}
        </button>

        <button
          type="button"
          onClick={onHangup}
          className="flex size-14 items-center justify-center rounded-full bg-red-600 text-white transition-colors hover:bg-red-700"
          aria-label={t`Hang up`}
        >
          <PhoneOff className="size-6" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/* ────────── States ────────── */

function RoomSkeleton() {
  return (
    <div className="bg-background min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-md space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-12 w-full rounded-xl" />
      </div>
    </div>
  );
}

function RoomError({
  message,
  onRetry,
  onBack,
}: {
  message: string;
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <div className="bg-background min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-md">
        <div className="border-destructive/20 bg-card rounded-2xl border p-6 shadow-sm">
          <PageHeader
            leading={
              <span className="bg-destructive-soft text-destructive flex size-10 shrink-0 items-center justify-center rounded-lg">
                <AlertTriangle className="size-5" aria-hidden />
              </span>
            }
            title={<Trans>Couldn't open video room</Trans>}
            description={message}
          />
          <div className="mt-5 flex flex-col gap-2">
            <Button size="touch" onClick={onRetry} className="w-full">
              <RotateCw className="size-4" aria-hidden />
              <Trans>Try again</Trans>
            </Button>
            <Button variant="outline" size="touch" onClick={onBack} className="w-full">
              <Trans>Back to dashboard</Trans>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
